import React, { useContext, useEffect, useReducer, useState, useMemo, useCallback } from 'react';
import { TUserInfoVisibleForm, userProfileInitialState, UserProfileContext } from './userProfileContext';
import { IDataGetUserInfo, IGetUserInfo } from 'types';
import { userProfileReducer } from './userProfileReducer';
import { UserProfileActionTypes } from './userProfileActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getUserInfo } from 'backend/users';

export const UserProfileState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(userProfileReducer, userProfileInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const [isMounted, setMounted] = useState<boolean>(false);

	const userID = loggedUser.userId;

	const { response, loading, refetch } = useFetchData<IGetUserInfo, string>(userID ? getUserInfo : null, userID);

	useEffect(() => {
		setMounted(true);
		if (isMounted) {
			refetch();
		}
		return () => {
			setMounted(false);
		};
	}, [userID]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		response &&
			dispatch({
				type: UserProfileActionTypes.SET_USER_FOR_USER_INFO,
				payload: {
					userForUserInfo: response.data.user,
				},
			});
	}, [response]);

	const setUserForUserInfo = useCallback((value: IDataGetUserInfo) => {
		dispatch({
			type: UserProfileActionTypes.SET_USER_FOR_USER_INFO,
			payload: {
				userForUserInfo: value,
			},
		});
	}, []);

	const changeUserInfoVisibleForm = useCallback((value: TUserInfoVisibleForm) => {
		dispatch({
			type: UserProfileActionTypes.CHANGE_USER_INFO_VISIBLE_FORM,
			payload: {
				userInfoVisibleForm: value,
			},
		});
	}, []);

	const contextValue = useMemo(
		() => ({
			setUserForUserInfo,
			changeUserInfoVisibleForm,
			userForUserInfo: state.userForUserInfo,
			userInfoVisibleForm: state.userInfoVisibleForm,
		}),
		[setUserForUserInfo, changeUserInfoVisibleForm, state.userForUserInfo, state.userInfoVisibleForm],
	);

	return <UserProfileContext.Provider value={contextValue}>{children}</UserProfileContext.Provider>;
};
